/**
 * O que a equipe escreveu pelo botão flutuante — a fila de "o que arrumar no
 * Sistema".
 *
 * **Abre nos abertos.** Diferente da aba dos clientes, que porta a tela antiga,
 * esta nasceu agora e nasce como fila de trabalho: o que já foi resolvido está a
 * um clique, mas não é o que se quer ver primeiro.
 *
 * **A tela de onde veio é um link.** Quem relata um problema estava numa tela;
 * abrir a mesma tela é o primeiro passo para reproduzir.
 *
 * As fotos vêm pela ApiSistema (que lê da ApiConteudo) com o token — por isso
 * não dá para pôr a URL direto no `<img>`, e cada miniatura baixa a sua.
 */

import { useCallback, useEffect, useState, type ReactNode } from "react"
import { Link } from "react-router-dom"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import {
  listarFeedbacksDoSistema, marcarFeedbackResolvido, urlDaImagemDoFeedback,
  type CodigoDoTipo, type FeedbackDoSistema, type Situacao,
} from "@/api/feedbackDoSistema"
import { BarraDeEstado } from "@/kit/BarraDeEstado"
import { Faixa } from "@/kit/Faixa"
import { D, Icone, IconeDoTipo } from "@/casca/iconesDoFeedback"
import { Botao, estiloCampo, mensagem } from "../outros/pecas"

const TIPOS: { codigo: CodigoDoTipo; plural: string }[] = [
  { codigo: "problema", plural: "Problemas" },
  { codigo: "ideia", plural: "Ideias" },
  { codigo: "elogio", plural: "Elogios" },
]

export function FeedbackDaEquipe({ abas }: { abas: ReactNode }) {
  const qc = useQueryClient()
  const [situacao, setSituacao] = useState<Situacao>("abertos")
  const [tipo, setTipo] = useState<CodigoDoTipo | "">("")
  const [pagina, setPagina] = useState(1)
  const [recado, setRecado] = useState<string | null>(null)
  const [galeria, setGaleria] = useState<{ ids: number[]; indice: number } | null>(null)

  const dados = useQuery({
    queryKey: ["feedback-sistema", tipo, situacao, pagina] as const,
    queryFn: () => listarFeedbacksDoSistema({ tipo, situacao, pagina }),
    placeholderData: (anterior) => anterior,
  })

  const resolver = useMutation({
    mutationFn: ({ id, resolvido }: { id: number; resolvido: boolean }) => marcarFeedbackResolvido(id, resolvido),
    onSuccess: (r) => {
      qc.invalidateQueries({ queryKey: ["feedback-sistema"] })
      setRecado(r.resolvido
        ? `Feedback #${r.id} marcado como resolvido.`
        : `Feedback #${r.id} reaberto.`)
    },
    onError: (e) => setRecado(mensagem(e)),
  })

  const contagem = dados.data?.contagem
  const itens = dados.data?.itens ?? []
  const paginas = dados.data?.totalPaginas ?? 0

  const mudar = (f: () => void) => { f(); setPagina(1) }

  return (
    <div style={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column" }}>
      <Faixa
        titulo={abas}
        detalhe={dados.data ? `${dados.data.totalNoFiltro} no filtro` : undefined}
      >
        <Segmentado
          valor={situacao}
          aoMudar={(v) => mudar(() => setSituacao(v))}
          opcoes={[
            { valor: "abertos", rotulo: "Abertos", conta: contagem?.abertos },
            { valor: "resolvidos", rotulo: "Resolvidos", conta: contagem?.resolvidos },
            { valor: "todos", rotulo: "Todos", conta: contagem?.total },
          ]}
        />
        <select
          value={tipo}
          onChange={(e) => mudar(() => setTipo(e.target.value as CodigoDoTipo | ""))}
          style={{ ...estiloCampo, width: 150 }}
          aria-label="Tipo"
        >
          <option value="">Todos os tipos</option>
          {TIPOS.map((t) => <option key={t.codigo} value={t.codigo}>{t.plural}</option>)}
        </select>
      </Faixa>

      <div style={{ flex: 1, minHeight: 0, overflowY: "auto", padding: "16px 18px 90px" }}>
        <div style={{ maxWidth: 920, margin: "0 auto", display: "flex", flexDirection: "column", gap: 14 }}>
          {contagem && (
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(170px, 1fr))", gap: 10 }}>
              {TIPOS.map((t) => (
                <CartaoDeTipo
                  key={t.codigo}
                  tipo={t.codigo}
                  rotulo={t.plural}
                  quantidade={contagem.porTipo[t.codigo] ?? 0}
                  ligado={tipo === t.codigo}
                  aoClicar={() => mudar(() => setTipo(tipo === t.codigo ? "" : t.codigo))}
                />
              ))}
            </div>
          )}

          {dados.isPending ? (
            <Esqueleto />
          ) : dados.error ? (
            <p style={{ fontSize: 12.5, color: "var(--erro)" }}>{mensagem(dados.error)}</p>
          ) : itens.length === 0 ? (
            <Vazio situacao={situacao} filtrado={tipo !== ""} />
          ) : (
            itens.map((f) => (
              <CartaoDoFeedback
                key={f.id}
                f={f}
                ocupado={resolver.isPending && resolver.variables?.id === f.id}
                aoResolver={(resolvido) => resolver.mutate({ id: f.id, resolvido })}
                aoAbrirImagem={(indice) => setGaleria({ ids: f.imagens.map((i) => i.id), indice })}
              />
            ))
          )}

          {paginas > 1 && (
            <div style={{ display: "flex", gap: 8, alignItems: "center", justifyContent: "center" }}>
              <Botao aoClicar={() => setPagina((p) => p - 1)} desligado={pagina <= 1}>Anterior</Botao>
              <span style={{ fontSize: 11.5, color: "var(--txt3)" }}>página {pagina} de {paginas}</span>
              <Botao aoClicar={() => setPagina((p) => p + 1)} desligado={pagina >= paginas}>Próxima</Botao>
            </div>
          )}
        </div>
      </div>

      <BarraDeEstado direita={contagem ? `${contagem.abertos} aberto(s) · ${contagem.resolvidos} resolvido(s)` : undefined}>
        {recado ?? "O que a equipe escreve pelo botão de feedback, no canto de qualquer tela."}
      </BarraDeEstado>

      {galeria && (
        <Galeria ids={galeria.ids} inicial={galeria.indice} aoFechar={() => setGaleria(null)} />
      )}
    </div>
  )
}

// ------------------------------------------------------------------ partes

export function Segmentado<T extends string>({ valor, aoMudar, opcoes }: {
  valor: T
  aoMudar: (v: T) => void
  opcoes: { valor: T; rotulo: string; conta?: number }[]
}) {
  return (
    <div role="radiogroup" style={{ display: "inline-flex", padding: 2, gap: 2, borderRadius: 8, border: "1px solid var(--line)", background: "var(--surface2)" }}>
      {opcoes.map((o) => {
        const ligado = o.valor === valor
        return (
          <button
            key={o.valor}
            type="button"
            role="radio"
            aria-checked={ligado}
            onClick={() => aoMudar(o.valor)}
            style={{
              display: "inline-flex", alignItems: "center", gap: 6, height: 26, padding: "0 10px",
              borderRadius: 6, fontSize: 12, fontWeight: ligado ? 600 : 500,
              color: ligado ? "var(--brand-on-soft)" : "var(--txt2)",
              background: ligado ? "var(--brand-soft)" : "transparent",
            }}
          >
            {o.rotulo}
            {o.conta != null && <span className="num" style={{ fontSize: 10.5, opacity: .8 }}>{o.conta}</span>}
          </button>
        )
      })}
    </div>
  )
}

function CartaoDeTipo({ tipo, rotulo, quantidade, ligado, aoClicar }: {
  tipo: CodigoDoTipo
  rotulo: string
  quantidade: number
  ligado: boolean
  aoClicar: () => void
}) {
  return (
    <button
      type="button"
      onClick={aoClicar}
      aria-pressed={ligado}
      className={`fb-${tipo}`}
      title={ligado ? "Mostrar todos os tipos" : `Só ${rotulo.toLowerCase()}`}
      style={{
        display: "flex", alignItems: "center", gap: 12, padding: "12px 14px", borderRadius: 12,
        textAlign: "left", background: ligado ? "var(--fb-soft)" : "var(--surface)",
        border: `1.5px solid ${ligado ? "var(--fb-cor)" : "var(--line)"}`,
        transition: "border-color .15s ease, background .15s ease",
      }}
    >
      <span style={{
        width: 38, height: 38, borderRadius: 11, display: "flex", alignItems: "center", justifyContent: "center",
        background: ligado ? "var(--surface)" : "var(--fb-soft)", color: "var(--fb-cor)",
      }}>
        <IconeDoTipo tipo={tipo} tamanho={20} />
      </span>
      <span>
        <span className="num" style={{ display: "block", fontSize: 22, fontWeight: 650, lineHeight: 1.1, color: "var(--txt)" }}>
          {quantidade}
        </span>
        <span style={{ fontSize: 12, color: ligado ? "var(--fb-on-soft)" : "var(--txt2)" }}>{rotulo}</span>
      </span>
    </button>
  )
}

/**
 * Um feedback. Sem `aoResolver` é **só leitura** — o das Solicitações, que qualquer pessoa
 * abre: sem o botão de resolver e sem o navegador de quem escreveu, que só interessa a
 * quem vai reproduzir o problema.
 */
export function CartaoDoFeedback({ f, ocupado = false, aoResolver, aoAbrirImagem, selo }: {
  f: FeedbackDoSistema
  ocupado?: boolean
  aoResolver?: (resolvido: boolean) => void
  aoAbrirImagem: (indice: number) => void
  /** Uma marca ao lado do autor — o "seu" das Solicitações. */
  selo?: ReactNode
}) {
  const leitura = !aoResolver
  const data = new Date(f.data)
  return (
    <article
      className={`fb-${f.tipo}`}
      style={{
        position: "relative", overflow: "hidden", borderRadius: 12, background: "var(--surface)",
        border: "1px solid var(--line)", boxShadow: "var(--sombra)",
        padding: "14px 16px 14px 20px", opacity: f.resolvido ? 0.78 : 1,
      }}
    >
      {/* A faixa colorida à esquerda: o tipo se lê de longe, antes do texto. */}
      <span aria-hidden="true" style={{ position: "absolute", left: 0, top: 0, bottom: 0, width: 4, background: "var(--fb-cor)" }} />

      <header style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap", marginBottom: 9 }}>
        <span style={{
          display: "inline-flex", alignItems: "center", gap: 5, height: 22, padding: "0 8px 0 6px",
          borderRadius: 6, background: "var(--fb-soft)", color: "var(--fb-on-soft)", fontSize: 11.5, fontWeight: 600,
        }}>
          <IconeDoTipo tipo={f.tipo} tamanho={14} />
          {f.tipoNome}
        </span>

        <Iniciais nome={f.autor} />
        <span style={{ fontSize: 12.5, fontWeight: 600 }}>{f.autor}</span>
        {selo}
        <time dateTime={f.data} title={data.toLocaleString("pt-BR")} style={{ fontSize: 11.5, color: "var(--txt3)" }}>
          {haQuanto(data)}
        </time>
        <span className="mono" style={{ fontSize: 11, color: "var(--txt3)" }}>#{f.id}</span>

        <span style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: 8 }}>
          {f.resolvido && (
            <span
              title={f.resolvidoEm ? `Em ${new Date(f.resolvidoEm).toLocaleString("pt-BR")}` : undefined}
              style={{
                display: "inline-flex", alignItems: "center", gap: 4, height: 22, padding: "0 8px",
                borderRadius: 999, background: "var(--ok-soft)", color: "var(--ok-on-soft)", fontSize: 11, fontWeight: 600,
              }}
            >
              <Icone d={D.check} tamanho={12} largura={2.6} />
              {leitura ? "Feito" : "Resolvido"}{f.resolvidoPorNome ? ` por ${f.resolvidoPorNome.split(/\s+/)[0]}` : ""}
              {leitura && f.resolvidoEm ? ` · ${new Date(f.resolvidoEm).toLocaleDateString("pt-BR")}` : ""}
            </span>
          )}
          {leitura && !f.resolvido && (
            <span style={{
              display: "inline-flex", alignItems: "center", gap: 4, height: 22, padding: "0 8px",
              borderRadius: 999, background: "var(--aviso-soft)", color: "var(--aviso-on-soft)", fontSize: 11, fontWeight: 600,
            }}>
              Em aberto
            </span>
          )}
          {aoResolver && <button
            type="button"
            disabled={ocupado}
            onClick={() => aoResolver(!f.resolvido)}
            style={{
              display: "inline-flex", alignItems: "center", gap: 6, height: 28, padding: "0 11px", borderRadius: 8,
              fontSize: 12, fontWeight: 500, border: "1px solid var(--line2)",
              background: f.resolvido ? "var(--surface)" : "var(--ok-soft)",
              color: f.resolvido ? "var(--txt2)" : "var(--ok-on-soft)",
              opacity: ocupado ? .6 : 1,
            }}
          >
            <Icone d={f.resolvido ? D.reabrir : D.check} tamanho={13} largura={2.2} />
            {ocupado ? "Salvando…" : f.resolvido ? "Reabrir" : "Marcar resolvido"}
          </button>}
        </span>
      </header>

      <p style={{ margin: 0, fontSize: 13, lineHeight: 1.6, whiteSpace: "pre-wrap", overflowWrap: "anywhere" }}>
        {f.mensagem}
      </p>

      {f.imagens.length > 0 && (
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginTop: 12 }}>
          {f.imagens.map((img, i) => (
            <Miniatura key={img.id} id={img.id} aoAbrir={() => aoAbrirImagem(i)} />
          ))}
        </div>
      )}

      {(f.pagina || (!leitura && f.navegador)) && (
        <footer style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap", marginTop: 12, fontSize: 11.5, color: "var(--txt3)" }}>
          {f.pagina && (
            <Link
              to={f.pagina}
              title="Abrir a tela de onde o feedback foi enviado"
              style={{ display: "inline-flex", alignItems: "center", gap: 5, minWidth: 0 }}
            >
              <Icone d={D.local} tamanho={13} />
              <code style={{ fontSize: 11, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: 380 }}>
                {f.pagina}
              </code>
            </Link>
          )}
          {!leitura && f.navegador && <span title={f.navegador}>{navegadorCurto(f.navegador)}</span>}
        </footer>
      )}
    </article>
  )
}

/** Baixa a foto de um id como URL de blob. */
export type CarregarImagem = (id: number, largura?: number) => Promise<string>

/**
 * Uma foto baixada com o token, como URL de blob — devolvida quando sai da tela.
 *
 * `carregar` diz de onde vem a foto (o padrão é o feedback). Ela entra nas dependências
 * do efeito: quem passa outra tem de mantê-la estável (`useCallback`), ou a foto é
 * baixada de novo a cada render.
 */
export function usarImagem(id: number, largura?: number, carregar: CarregarImagem = urlDaImagemDoFeedback) {
  const [url, setUrl] = useState<string | null>(null)
  const [falhou, setFalhou] = useState(false)

  useEffect(() => {
    let vivo = true
    let criada: string | null = null
    setUrl(null)
    setFalhou(false)
    carregar(id, largura)
      .then((u) => { criada = u; if (vivo) setUrl(u); else URL.revokeObjectURL(u) })
      .catch(() => { if (vivo) setFalhou(true) })
    return () => { vivo = false; if (criada) URL.revokeObjectURL(criada) }
  }, [id, largura, carregar])

  return { url, falhou }
}

function Miniatura({ id, aoAbrir }: { id: number; aoAbrir: () => void }) {
  const { url, falhou } = usarImagem(id, 240)
  return (
    <button
      type="button"
      onClick={aoAbrir}
      disabled={falhou}
      title={falhou ? "A foto não foi encontrada no servidor de arquivos" : "Ampliar"}
      style={{
        width: 104, height: 76, borderRadius: 9, overflow: "hidden", border: "1px solid var(--line2)",
        background: "var(--surface3)", display: "flex", alignItems: "center", justifyContent: "center",
        color: "var(--txt3)", cursor: falhou ? "default" : "zoom-in",
      }}
    >
      {url ? (
        <img src={url} alt="" style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }} />
      ) : falhou ? (
        <Icone d={D.alerta} tamanho={18} />
      ) : (
        <span className="fb-giro" aria-label="carregando" />
      )}
    </button>
  )
}

export function Galeria({ ids, inicial, aoFechar, carregar, rotulo = "Foto do feedback", prefixo = "feedback-imagem" }: {
  ids: number[]
  inicial: number
  aoFechar: () => void
  /** De onde vêm as fotos; estável, como pede o `usarImagem`. O padrão é o feedback. */
  carregar?: CarregarImagem
  rotulo?: string
  /** Início do nome do arquivo baixado, antes do id. */
  prefixo?: string
}) {
  const [indice, setIndice] = useState(inicial)
  const { url, falhou } = usarImagem(ids[indice]!, undefined, carregar)
  const anterior = useCallback(() => setIndice((i) => (i - 1 + ids.length) % ids.length), [ids.length])
  const proxima = useCallback(() => setIndice((i) => (i + 1) % ids.length), [ids.length])

  useEffect(() => {
    const tecla = (e: KeyboardEvent) => {
      if (e.key === "Escape") aoFechar()
      if (e.key === "ArrowLeft") anterior()
      if (e.key === "ArrowRight") proxima()
    }
    document.addEventListener("keydown", tecla)
    return () => document.removeEventListener("keydown", tecla)
  }, [aoFechar, anterior, proxima])

  const botao: React.CSSProperties = {
    width: 40, height: 40, borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center",
    background: "rgba(255,255,255,.12)", color: "#FFFFFF",
  }

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={rotulo}
      onClick={aoFechar}
      style={{
        position: "fixed", inset: 0, zIndex: 90, background: "rgba(8, 8, 14, .86)",
        display: "flex", alignItems: "center", justifyContent: "center", gap: 16, padding: 24,
        animation: "fb-entrar .18s ease",
      }}
    >
      {ids.length > 1 && (
        <button type="button" aria-label="Anterior" style={botao} onClick={(e) => { e.stopPropagation(); anterior() }}>
          <Icone d={D.voltar} tamanho={20} largura={2.2} />
        </button>
      )}

      <div onClick={(e) => e.stopPropagation()} style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 10, minWidth: 0 }}>
        {url ? (
          <img src={url} alt="" style={{ maxWidth: "min(1400px, 84vw)", maxHeight: "80vh", borderRadius: 10, boxShadow: "0 20px 60px rgba(0,0,0,.5)", background: "#FFFFFF" }} />
        ) : falhou ? (
          <p style={{ color: "#FFFFFF", fontSize: 13 }}>A foto não foi encontrada no servidor de arquivos.</p>
        ) : (
          <span className="fb-giro" style={{ color: "#FFFFFF", width: 26, height: 26 }} />
        )}
        <div style={{ display: "flex", alignItems: "center", gap: 14, color: "rgba(255,255,255,.75)", fontSize: 12 }}>
          {ids.length > 1 && <span className="num">{indice + 1} de {ids.length}</span>}
          {url && (
            <a href={url} download={`${prefixo}-${ids[indice]}`} style={{ display: "inline-flex", alignItems: "center", gap: 5, color: "#FFFFFF" }}>
              <Icone d={D.baixar} tamanho={14} /> Baixar
            </a>
          )}
          <span>Esc fecha</span>
        </div>
      </div>

      {ids.length > 1 && (
        <button type="button" aria-label="Próxima" style={botao} onClick={(e) => { e.stopPropagation(); proxima() }}>
          <Icone d={D.seta} tamanho={20} largura={2.2} />
        </button>
      )}

      <button
        type="button"
        aria-label="Fechar"
        onClick={aoFechar}
        style={{ ...botao, position: "absolute", top: 18, right: 18 }}
      >
        <Icone d={D.fechar} tamanho={18} largura={2.2} />
      </button>
    </div>
  )
}

function Iniciais({ nome }: { nome: string }) {
  const letras = nome.trim().split(/\s+/).slice(0, 2).map((p) => p[0] ?? "").join("").toUpperCase() || "··"
  return (
    <span
      aria-hidden="true"
      style={{
        width: 24, height: 24, borderRadius: "50%", display: "inline-flex", alignItems: "center", justifyContent: "center",
        background: "var(--brand-soft)", color: "var(--brand-on-soft)", fontSize: 10, fontWeight: 700,
      }}
    >
      {letras}
    </span>
  )
}

function Vazio({ situacao, filtrado }: { situacao: Situacao; filtrado: boolean }) {
  const titulo = situacao === "abertos" && !filtrado
    ? "Nada esperando — fila limpa."
    : "Nenhum feedback neste filtro."
  return (
    <div style={{
      display: "flex", flexDirection: "column", alignItems: "center", gap: 8, padding: "48px 16px",
      borderRadius: 12, border: "1.5px dashed var(--line2)", color: "var(--txt3)", textAlign: "center",
    }}>
      <span style={{
        width: 48, height: 48, borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center",
        background: "var(--surface3)", color: "var(--txt2)",
      }}>
        <Icone d={D.balao} tamanho={24} />
      </span>
      <strong style={{ fontSize: 13.5, color: "var(--txt)" }}>{titulo}</strong>
      <span style={{ fontSize: 12.5, maxWidth: "46ch", lineHeight: 1.55 }}>
        Qualquer pessoa da equipe pode mandar um feedback pelo botão laranja no canto de baixo da tela.
      </span>
    </div>
  )
}

export function Esqueleto() {
  return (
    <>
      {[0, 1, 2].map((i) => (
        <div key={i} style={{ height: 118, borderRadius: 12, background: "var(--surface)", border: "1px solid var(--line)", opacity: 1 - i * 0.25 }} />
      ))}
    </>
  )
}

function haQuanto(d: Date) {
  const s = Math.round((Date.now() - d.getTime()) / 1000)
  if (s < 60) return "agora há pouco"
  const m = Math.round(s / 60)
  if (m < 60) return `há ${m} min`
  const h = Math.round(m / 60)
  if (h < 24) return `há ${h} h`
  const dias = Math.round(h / 24)
  if (dias < 7) return dias === 1 ? "ontem" : `há ${dias} dias`
  return d.toLocaleDateString("pt-BR")
}

/** "Chrome 131 · Windows" em vez da linha inteira do user agent. */
function navegadorCurto(ua: string) {
  const nav =
    /Edg\/(\d+)/.exec(ua)?.[1] ? `Edge ${/Edg\/(\d+)/.exec(ua)![1]}`
      : /Firefox\/(\d+)/.exec(ua)?.[1] ? `Firefox ${/Firefox\/(\d+)/.exec(ua)![1]}`
        : /Chrome\/(\d+)/.exec(ua)?.[1] ? `Chrome ${/Chrome\/(\d+)/.exec(ua)![1]}`
          : /Safari\//.test(ua) ? "Safari" : "Navegador"
  const so = /Windows/.test(ua) ? "Windows" : /Android/.test(ua) ? "Android"
    : /iPhone|iPad/.test(ua) ? "iOS" : /Mac OS X/.test(ua) ? "macOS" : /Linux/.test(ua) ? "Linux" : ""
  return so ? `${nav} · ${so}` : nav
}
